import React, { useState, useEffect } from 'react';
import { db, auth, getCollectionName } from './firebase';
import {
  collection, onSnapshot, updateDoc, deleteDoc, doc,
  query, where, orderBy, serverTimestamp, arrayUnion, getDoc
} from './firestore-compat';
import { useNavigate } from 'react-router-dom';
import { speakAvaliacaoConfirmada, speakStatusChange } from './utils/speechUtils';

const C = { primary:'#6366f1', text:'#1e293b', muted:'#64748b', border:'#e2e8f0', white:'#fff', bg:'#f8fafc' };

const fmtData = (data) => {
  if (!data) return '—';
  const d = new Date(data + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'2-digit', year:'numeric' });
};

const today = () => new Date().toISOString().split('T')[0];

const TIPO_COLOR = {
  avaliacao:   { border:'#3b82f6', bg:'#eff6ff', label:'Avaliação',   dot:'#3b82f6' },
  implantacao: { border:'#0891b2', bg:'#ecfeff', label:'Implantação', dot:'#0891b2' },
};

// Ordem dos status no funil — usada para não regredir paciente
const STATUS_ORDER = [
  'Recebido','Aguardando Avaliação','Em Avaliação','Avaliação Concluída',
  'Em Implantação','Implantado','Alta',
];

export default function AgendaCaptacao() {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [confirming, setConfirming]     = useState(null);
  const [tab, setTab]                   = useState('avaliacao'); // avaliacao | implantacao | historico
  const [isAdmin, setIsAdmin]           = useState(false);
  const [rescheduling, setRescheduling] = useState(null); // agId being rescheduled
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [deleting, setDeleting]         = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, getCollectionName('usuarios_v2'), user.uid)).then(snap => {
      if (snap.exists()) {
        const roles = Array.isArray(snap.data().roles) ? snap.data().roles : (snap.data().role ? [snap.data().role] : []);
        setIsAdmin(roles.includes('admin') || roles.includes('administrador'));
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user?.email) return;

    let unsubById = () => {};
    let unsubByEmail = () => {};
    const mergeMap = new Map();

    const flush = () => {
      setAgendamentos([...mergeMap.values()].sort((a, b) => (a.data > b.data ? 1 : -1)));
      setLoading(false);
    };

    // Busca profissionalId do usuário na lista de profissionais cadastrados
    getDoc(doc(db, getCollectionName('captacao_config'), 'profissionais')).then(snap => {
      const lista = snap.exists() ? (snap.data().lista || []) : [];
      const meuPerfil = lista.find(p => p.email?.toLowerCase() === user.email?.toLowerCase());

      // Query 1: por profissionalEmail (registros novos)
      const qEmail = query(
        collection(db, getCollectionName('captacao_agendamentos')),
        where('profissionalEmail', '==', user.email),
        orderBy('data', 'asc')
      );
      unsubByEmail = onSnapshot(qEmail, snap => {
        snap.docs.forEach(d => mergeMap.set(d.id, { id: d.id, ...d.data() }));
        flush();
      }, () => setLoading(false));

      // Query 2: por profissionalId (registros antigos sem email)
      if (meuPerfil?.id) {
        const qId = query(
          collection(db, getCollectionName('captacao_agendamentos')),
          where('profissionalId', '==', meuPerfil.id),
          orderBy('data', 'asc')
        );
        unsubById = onSnapshot(qId, snap => {
          snap.docs.forEach(d => mergeMap.set(d.id, { id: d.id, ...d.data() }));
          flush();
        }, () => {});
      }
    }).catch(() => setLoading(false));

    return () => { unsubByEmail(); unsubById(); };
  }, [user]);

  const isPending  = a => a.status === 'agendado' || a.status === 'em_avaliacao';
  const avaliacoes   = agendamentos.filter(a => isPending(a) && a.tipoAgenda !== 'implantacao');
  const implantacoes = agendamentos.filter(a => isPending(a) && a.tipoAgenda === 'implantacao');
  const pendentes    = [...avaliacoes, ...implantacoes]; // total para o header
  const historico    = agendamentos.filter(a => a.status === 'confirmado' || a.status === 'cancelado');

  const iniciarAvaliacao = async (ag) => {
    setConfirming(ag.id);
    try {
      await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.id), {
        status: 'em_avaliacao',
        dataInicio: serverTimestamp(),
      });
      // Avança o paciente para "Em Avaliação" na captação
      const entry = { status:'Em Avaliação', timestamp:new Date().toISOString(), responsavel:user.email, dados:{ iniciadoPor:user.email } };
      await updateDoc(doc(db, getCollectionName('captacao_pacientes'), ag.pacienteId), {
        status: 'Em Avaliação',
        historico: arrayUnion(entry),
        dataAtualizacao: serverTimestamp(),
      });
      speakStatusChange(ag.pacienteNome, 'Em Avaliação');
    } catch (e) { console.error(e); }
    setConfirming(null);
  };

  const concluirAvaliacao = async (ag) => {
    setConfirming(ag.id);
    try {
      await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.id), {
        status: 'confirmado',
        dataConfirmacao: serverTimestamp(),
      });
      if (ag.pacienteId) {
        // Verificar status atual do paciente para não regredir no funil
        const pacSnap = await getDoc(doc(db, getCollectionName('captacao_pacientes'), ag.pacienteId));
        const currentStatus = pacSnap.exists() ? pacSnap.data().status : null;
        const currentIdx = STATUS_ORDER.indexOf(currentStatus);
        const targetIdx  = STATUS_ORDER.indexOf('Avaliação Concluída');

        // Só avança se o paciente ainda estiver em "Em Avaliação" ou etapa anterior
        if (currentIdx < 0 || currentIdx <= targetIdx) {
          const entry = { status:'Avaliação Concluída', timestamp:new Date().toISOString(), responsavel:user.email, dados:{ concluidoPor:user.email } };
          await updateDoc(doc(db, getCollectionName('captacao_pacientes'), ag.pacienteId), {
            status: 'Avaliação Concluída',
            historico: arrayUnion(entry),
            dataAtualizacao: serverTimestamp(),
          });
        }
        // Se já passou de "Avaliação Concluída" (Em Implantação, Implantado…), não mexe
      }
      speakAvaliacaoConfirmada(ag.pacienteNome, user.displayName || user.email.split('@')[0]);
    } catch (e) { console.error(e); }
    setConfirming(null);
  };

  const handleReschedule = async (ag) => {
    if (!rescheduleDate) return;
    setConfirming(ag.id);
    try {
      await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.id), {
        data: rescheduleDate,
        atualizadoEm: serverTimestamp(),
      });
      setRescheduling(null);
      setRescheduleDate('');
    } catch (e) { console.error(e); }
    setConfirming(null);
  };

  const handleDelete = async (ag) => {
    if (!window.confirm(`Excluir agendamento de ${ag.pacienteNome} em ${ag.data}?`)) return;
    setDeleting(ag.id);
    try {
      await deleteDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.id));
    } catch (e) { console.error(e); }
    setDeleting(null);
  };

  const lista = tab === 'avaliacao' ? avaliacoes : tab === 'implantacao' ? implantacoes : historico;

  // Agrupado por data
  const byDate = {};
  lista.forEach(a => { if (!byDate[a.data]) byDate[a.data] = []; byDate[a.data].push(a); });
  const sortedDates = Object.keys(byDate).sort();

  const todayStr = today();

  if (!user) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#f8fafc' }}>
      <div style={{ textAlign:'center', color:C.muted }}>
        <div style={{ fontSize:'3rem', marginBottom:16 }}>🔒</div>
        <p style={{ fontWeight:700, fontSize:'1.1rem' }}>Acesso restrito</p>
        <button onClick={()=>navigate('/login')} style={{ background:C.primary, color:'#fff', border:'none', borderRadius:10, padding:'10px 24px', cursor:'pointer', fontWeight:700, marginTop:12 }}>Fazer Login</button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:480, margin:'0 auto', minHeight:'100vh', background:'#f8fafc', fontFamily:"'Inter',sans-serif" }}>
      {/* Header */}
      <div style={{ background:`linear-gradient(135deg, ${C.primary}, #8b5cf6)`, padding:'20px 20px 16px', color:'#fff' }}>
        <button onClick={()=>navigate(-1)} style={{ background:'rgba(255,255,255,0.2)', border:'none', borderRadius:8, color:'#fff', padding:'4px 12px', cursor:'pointer', fontSize:'0.82rem', marginBottom:12 }}>← Voltar</button>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(255,255,255,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.3rem' }}>👩‍⚕️</div>
          <div>
            <h2 style={{ margin:0, fontSize:'1.1rem', fontWeight:800 }}>Minha Agenda</h2>
            <p style={{ margin:0, fontSize:'0.78rem', opacity:0.8 }}>{user.displayName || user.email}</p>
          </div>
        </div>
        {/* Stats */}
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          {[
            { label:'Pendentes', value:pendentes.length, bg:'rgba(255,255,255,0.2)' },
            { label:'Hoje', value:pendentes.filter(a=>a.data===todayStr).length, bg:'rgba(255,255,255,0.2)' },
            { label:'Concluídas', value:historico.filter(a=>a.status==='confirmado').length, bg:'rgba(255,255,255,0.2)' },
          ].map(s=>(
            <div key={s.label} style={{ flex:1, background:s.bg, borderRadius:10, padding:'8px 12px', textAlign:'center' }}>
              <div style={{ fontWeight:800, fontSize:'1.3rem' }}>{s.value}</div>
              <div style={{ fontSize:'0.65rem', opacity:0.85 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'0 20px' }}>
        {[
          {k:'avaliacao',   l:`Avaliação (${avaliacoes.length})`,   color:'#3b82f6'},
          {k:'implantacao', l:`Implantação (${implantacoes.length})`,color:'#0891b2'},
          {k:'historico',   l:'Histórico',                           color:C.primary},
        ].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
            style={{ flex:1, padding:'12px 0', border:'none', background:'none', cursor:'pointer', fontWeight:tab===t.k?700:500, fontSize:'0.78rem', color:tab===t.k?t.color:C.muted, borderBottom:tab===t.k?`2px solid ${t.color}`:'2px solid transparent', marginBottom:-1 }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:'16px 16px' }}>
        {loading && <div style={{ textAlign:'center', padding:'40px', color:C.muted }}>Carregando...</div>}

        {!loading && sortedDates.length === 0 && (
          <div style={{ textAlign:'center', padding:'48px 20px', color:C.muted }}>
            <div style={{ fontSize:'3rem', marginBottom:12 }}>📅</div>
            <p style={{ fontWeight:700, margin:0 }}>{tab==='historico'?'Nenhum histórico':`Nenhuma ${tab==='avaliacao'?'avaliação':'implantação'} pendente`}</p>
            <p style={{ fontSize:'0.82rem', margin:'4px 0 0' }}>{tab==='historico'?'Suas visitas confirmadas aparecerão aqui':'Você está em dia! 🎉'}</p>
          </div>
        )}

        {sortedDates.map(data => {
          const isToday = data === todayStr;
          const isPast  = data < todayStr;
          const items   = byDate[data];
          return (
            <div key={data} style={{ marginBottom:20 }}>
              {/* Date header */}
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <div style={{ background:isToday?C.primary:isPast?'#ef4444':'#f1f5f9', color:isToday||isPast?'#fff':C.muted, borderRadius:8, padding:'4px 12px', fontSize:'0.75rem', fontWeight:700 }}>
                  {isToday ? '📍 Hoje' : isPast ? '⚠️ Atrasado' : fmtData(data)}
                </div>
                <div style={{ flex:1, height:1, background:C.border }} />
              </div>

              {items.map(ag => {
                const tipo = TIPO_COLOR[ag.tipoAgenda] || TIPO_COLOR.avaliacao;
                const isRescheduling = rescheduling === ag.id;
                return (
                <div key={ag.id} style={{ background:'#fff', borderRadius:14, padding:16, marginBottom:12, border:`1px solid ${C.border}`, borderLeft:`4px solid ${tipo.border}`, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                  {/* Tipo + Horário + Status */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ background:`${C.primary}15`, color:C.primary, borderRadius:8, padding:'4px 12px', fontWeight:800, fontSize:'0.9rem' }}>
                        🕐 {ag.hora || '—'}
                      </div>
                      <span style={{ background:tipo.bg, color:tipo.border, border:`1px solid ${tipo.border}40`, borderRadius:20, padding:'3px 10px', fontSize:'0.7rem', fontWeight:700 }}>{tipo.label}</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      {ag.status === 'em_avaliacao' && (
                        <span style={{ background:'#eff6ff', color:'#1d4ed8', border:'1px solid #bfdbfe', borderRadius:20, padding:'3px 10px', fontSize:'0.72rem', fontWeight:700 }}>🔵 Em Avaliação</span>
                      )}
                      {ag.status === 'confirmado' && (
                        <span style={{ background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:20, padding:'3px 10px', fontSize:'0.72rem', fontWeight:700 }}>✅ Concluído</span>
                      )}
                      {isAdmin && (
                        <button onClick={() => handleDelete(ag)} disabled={deleting === ag.id}
                          style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:'1rem', padding:'2px 4px', opacity:deleting===ag.id?0.5:1 }} title="Excluir agendamento">🗑️</button>
                      )}
                    </div>
                  </div>

                  {/* Paciente — dados completos */}
                  <div style={{ background:'#f8fafc', borderRadius:10, padding:'12px 14px', marginBottom:12, border:`1px solid ${C.border}` }}>
                    <div style={{ fontWeight:800, fontSize:'1rem', color:C.text, marginBottom:2 }}>{ag.pacienteNome}</div>
                    <div style={{ fontSize:'0.78rem', color:C.muted, marginBottom:8 }}>{ag.pacienteNumero} · {ag.convenio}{ag.pacienteConvenioEspecial && ag.pacienteConvenioEspecial!=='Nenhum'?` · 🏭 ${ag.pacienteConvenioEspecial}`:''}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:'#4f46e5', fontWeight:700 }}><span>🚀</span><span><strong>Tipo:</strong> {ag.pacienteTipoImplantacao === 'Reimplantação' ? 'Reimplantação' : 'Implantação'}</span></div>
                      <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:C.text }}><span>📍</span><span><strong>Local da Avaliação:</strong> {ag.hospital || ag.pacienteEndereco || '—'}</span></div>
                      {ag.hospital && (
                        <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:C.text }}><span>🏥</span><span><strong>Hospital:</strong> {ag.hospital}</span></div>
                      )}
                      {ag.pacienteEndereco && (
                        <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:C.text }}><span>🏠</span><span><strong>Endereço:</strong> {ag.pacienteEndereco}{ag.pacienteCep?` (CEP: ${ag.pacienteCep})`:''}</span></div>
                      )}
                      {ag.pacienteDataNascimento && (
                        <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:C.text }}><span>🎂</span><span><strong>Nascimento:</strong> {ag.pacienteDataNascimento.split('-').reverse().join('/')}</span></div>
                      )}
                      <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:C.text }}><span>🩺</span><span><strong>Filial:</strong> {ag.pacienteFilial} {ag.pacienteVm?'· ⚠️ VM':''}</span></div>
                      {ag.pacienteLiminar && (
                        <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:'#7c3aed' }}><span>⚖️</span><span><strong>Possui Liminar</strong></span></div>
                      )}
                      {ag.pacienteObservacoes && (
                        <div style={{ display:'flex', gap:6, fontSize:'0.78rem', color:C.muted }}><span>📝</span><span>{ag.pacienteObservacoes}</span></div>
                      )}
                    </div>
                  </div>

                  {/* Reagendamento inline */}
                  {isRescheduling && (
                    <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center' }}>
                      <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                        style={{ flex:1, padding:'10px 12px', border:'1px solid #0ea5e9', borderRadius:10, fontSize:'0.9rem', outline:'none' }} />
                      <button onClick={() => handleReschedule(ag)} disabled={!rescheduleDate || confirming === ag.id}
                        style={{ padding:'10px 16px', background:'#0891b2', color:'#fff', border:'none', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:'0.85rem', opacity:!rescheduleDate||confirming===ag.id?0.5:1 }}>
                        {confirming === ag.id ? '⏳' : 'Salvar'}
                      </button>
                      <button onClick={() => { setRescheduling(null); setRescheduleDate(''); }}
                        style={{ padding:'10px 14px', background:'#f1f5f9', color:C.muted, border:'none', borderRadius:10, cursor:'pointer', fontWeight:700 }}>✕</button>
                    </div>
                  )}
                  {/* Ação */}
                  {ag.status === 'agendado' && !isRescheduling && (
                    <div style={{ display:'flex', gap:8 }}>
                      <button
                        onClick={() => iniciarAvaliacao(ag)}
                        disabled={confirming === ag.id}
                        style={{ flex:1, padding:'14px', background:'#2563eb', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', fontWeight:800, fontSize:'0.95rem', boxShadow:'0 4px 12px rgba(37,99,235,0.35)', opacity:confirming===ag.id?0.7:1, transition:'all 0.15s' }}>
                        {confirming === ag.id ? '⏳ Iniciando...' : '▶️ Iniciar Avaliação'}
                      </button>
                      <button
                        onClick={() => { setRescheduling(ag.id); setRescheduleDate(ag.data || ''); }}
                        style={{ padding:'14px 16px', background:'#f0f9ff', color:'#0891b2', border:'1px solid #bae6fd', borderRadius:12, cursor:'pointer', fontWeight:700, fontSize:'0.9rem' }}
                        title="Reagendar">📅</button>
                    </div>
                  )}
                  {ag.status === 'em_avaliacao' && (
                    <button
                      onClick={() => concluirAvaliacao(ag)}
                      disabled={confirming === ag.id}
                      style={{ width:'100%', padding:'14px', background:'#16a34a', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', fontWeight:800, fontSize:'0.95rem', boxShadow:'0 4px 12px rgba(22,163,74,0.35)', opacity:confirming===ag.id?0.7:1, transition:'all 0.15s' }}>
                      {confirming === ag.id ? '⏳ Concluindo...' : '✅ Concluir Avaliação'}
                    </button>
                  )}
                </div>
              );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
